import { Alert, Box, Button, DataTable, Drawer, IconButton, Stack, Tooltip } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import {
  Plus as AddIcon,
  ArrowDown,
  ArrowUp,
  ContentCopy,
  Pencil as PencilIcon,
  TrashCan as TrashIcon,
  EyeOutline as VisibilityIcon,
  EyeOffOutline as VisibilityOffIcon,
} from 'mdi-material-ui';
import { useMemo, useState } from 'react';
import { useImmer } from 'use-immer';
import { EventDefinition } from '@/core';
import { Action, useListPluginMetadata } from '@/plugin-system';
import { useDiscardChangesConfirmationDialog } from '../../context';
import { DEFAULT_COLUMN_PROPS, DEFAULT_TABLE_PROPS } from '../../utils/tables';
import { EventEditForm } from './EventEditForm';

function getValidation(eventDefinitions: EventDefinition[]) {
  const errors = [];

  /**  Event names must be unique */
  const eventNames = eventDefinitions.map((eventDefinition) => eventDefinition.spec.name);
  const uniqueEventNames = new Set(eventNames);
  if (eventNames.length !== uniqueEventNames.size) {
    errors.push('Event names must be unique');
  }
  return {
    errors: errors,
    isValid: errors.length === 0,
  };
}

function EventPluginDisplayName({ kind }: { kind: string }) {
  const results = useListPluginMetadata('EventQuery');
  return <>{results.data?.find((p) => p.kind === kind)?.display?.name ?? kind}</>;
}

export function EventEditor(props: {
  eventDefinitions: EventDefinition[];
  onChange: (eventDefinitions: EventDefinition[]) => void;
  onCancel: () => void;
}) {
  const [eventDefinitions, setEventDefinitions] = useImmer(props.eventDefinitions);
  const [eventEditIdx, setEventEditIdx] = useState<number | null>(null);
  const [eventFormAction, setEventFormAction] = useState<Action>('update');
  const { enableChangeEventsUi } = useFeatureFlags();

  const validation = useMemo(() => getValidation(eventDefinitions), [eventDefinitions]);
  const currentEditingEventDefinition = typeof eventEditIdx === 'number' && eventDefinitions[eventEditIdx];

  const { openDiscardChangesConfirmationDialog, closeDiscardChangesConfirmationDialog } =
    useDiscardChangesConfirmationDialog();
  const handleCancel = () => {
    if (JSON.stringify(props.eventDefinitions) !== JSON.stringify(eventDefinitions)) {
      openDiscardChangesConfirmationDialog({
        onDiscardChanges: () => {
          closeDiscardChangesConfirmationDialog();
          props.onCancel();
        },
        onCancel: () => {
          closeDiscardChangesConfirmationDialog();
        },
        description:
          'You have unapplied changes. Are you sure you want to discard these changes? Changes cannot be recovered.',
      });
    } else {
      props.onCancel();
    }
  };

  const removeEvent = (index: number) => {
    setEventDefinitions((draft) => {
      draft.splice(index, 1);
    });
  };

  const addEvent = () => {
    setEventFormAction('create');
    setEventDefinitions((draft) => {
      draft.push({
        kind: 'QueryEvent',
        spec: {
          name: 'NewEvent',
          plugin: {
            kind:
              enableChangeEventsUi === 'enabled' || enableChangeEventsUi === 'read-only'
                ? 'TimelineEventQuery'
                : 'PrometheusEventQuery',
            spec: {
              query: '',
            },
          },
        },
      });
    });
    setEventEditIdx(eventDefinitions.length);
  };

  const editEvent = (index: number) => {
    setEventFormAction('update');
    setEventEditIdx(index);
  };

  const toggleEventVisibility = (index: number, visible: boolean) => {
    setEventDefinitions((draft) => {
      const v = draft[index];
      if (!v) {
        return;
      }
      if (!v.spec.display) {
        v.spec.display = {
          name: v.spec.name,
          hidden: false,
        };
      }
      v.spec.display.hidden = visible === false;
    });
  };

  const changeEventOrder = (index: number, direction: 'up' | 'down') => {
    setEventDefinitions((draft) => {
      if (direction === 'up') {
        const prevElement = draft[index - 1];
        const currentElement = draft[index];
        if (index === 0 || !prevElement || !currentElement) {
          return;
        }
        draft[index - 1] = currentElement;
        draft[index] = prevElement;
      } else {
        const nextElement = draft[index + 1];
        const currentElement = draft[index];
        if (index === draft.length - 1 || !nextElement || !currentElement) {
          return;
        }
        draft[index + 1] = currentElement;
        draft[index] = nextElement;
      }
    });
  };

  const duplicateEvent = (index: number) => {
    setEventDefinitions((draft) => {
      const originalEvent = draft[index];
      if (!originalEvent) {
        return;
      }

      // Create a duplicate event by copying the original and modifying the name
      const duplicatedEvent = { ...originalEvent };
      duplicatedEvent.spec = { ...originalEvent.spec };
      duplicatedEvent.spec.name = `${originalEvent.spec.name} (copy)`;

      // Insert the duplicated event after the original
      draft.splice(index + 1, 0, duplicatedEvent);
    });
  };

  return (
    <>
      {currentEditingEventDefinition && (
        <EventEditForm
          initialEventDefinition={currentEditingEventDefinition}
          onChange={(definition) => {
            setEventDefinitions((draft) => {
              draft[eventEditIdx] = definition;
              setEventEditIdx(null);
            });
          }}
          onCancel={() => {
            if (eventFormAction === 'create') {
              removeEvent(eventEditIdx);
            }
            setEventEditIdx(null);
          }}
          action={eventFormAction}
        />
      )}
      {!currentEditingEventDefinition && (
        <>
          <Drawer.Header
            title="Dashboard events"
            primaryButtonDisabled={props.eventDefinitions === eventDefinitions || !validation.isValid}
            onPrimaryButtonClick={() => {
              props.onChange(eventDefinitions);
            }}
            onSecondaryButtonClick={handleCancel}
            primaryButtonText="Apply"
          />
          <Drawer.Content>
            <Stack spacing={2}>
              {!validation.isValid &&
                validation.errors.map((error) => (
                  <Alert severity="error" key={error}>
                    {error}
                  </Alert>
                ))}
              <DataTable
                {...DEFAULT_TABLE_PROPS}
                localeText={{
                  noRowsLabel: 'No events',
                }}
                getRowId={(row) => row.idx}
                columns={[
                  {
                    ...DEFAULT_COLUMN_PROPS,
                    field: 'Visibility',
                    width: 80,
                    renderCell: ({ row }) => {
                      const eventDefinition = row.definition;
                      const idx = row.idx;
                      return (
                        <Tooltip Content={eventDefinition.spec.display?.hidden === true ? 'Show event' : 'Hide event'}>
                          <IconButton
                            onClick={() => {
                              toggleEventVisibility(idx, eventDefinition.spec.display?.hidden === true);
                            }}
                          >
                            {eventDefinition.spec.display?.hidden === true ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    ...DEFAULT_COLUMN_PROPS,
                    field: 'Name',
                    flex: 1,
                    renderCell: ({ row }) => {
                      const eventDefinition = row.definition;
                      return <Box fontWeight="bold">{eventDefinition.spec.name}</Box>;
                    },
                  },
                  {
                    ...DEFAULT_COLUMN_PROPS,
                    field: 'Datasource',
                    flex: 1,
                    renderCell: ({ row }) => {
                      const eventDefinition = row.definition;
                      return <EventPluginDisplayName kind={eventDefinition.spec.plugin.kind} />;
                    },
                  },
                  {
                    ...DEFAULT_COLUMN_PROPS,
                    field: 'Actions',
                    headerName: '',
                    align: 'right',
                    width: 200,
                    renderCell: ({ row }) => {
                      const idx = row.idx;
                      return (
                        <div>
                          <IconButton onClick={() => changeEventOrder(idx, 'up')} disabled={idx === 0}>
                            <ArrowUp />
                          </IconButton>
                          <IconButton
                            onClick={() => changeEventOrder(idx, 'down')}
                            disabled={idx === eventDefinitions.length - 1}
                          >
                            <ArrowDown />
                          </IconButton>
                          <Tooltip Content="Duplicate event">
                            <IconButton onClick={() => duplicateEvent(idx)}>
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                          <Tooltip Content="Edit event">
                            <IconButton onClick={() => editEvent(idx)}>
                              <PencilIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip Content="Remove event">
                            <IconButton onClick={() => removeEvent(idx)}>
                              <TrashIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      );
                    },
                  },
                ]}
                rows={eventDefinitions.map((v, idx) => ({
                  definition: v,
                  idx,
                }))}
              />

              <Box display="flex">
                <Button variant="secondary" StartIcon={<AddIcon />} sx={{ marginLeft: 'auto' }} onClick={addEvent}>
                  Add event
                </Button>
              </Box>
            </Stack>
          </Drawer.Content>
        </>
      )}
    </>
  );
}
